/**
 * RegularPolygonGesture — the shared regular-polygon drawing state machine
 * (#928 / #931 / #934), extracted from the construction Draw controller so
 * the leather Shape / Cutout / Stitch tools inherit the exact same gesture
 * set instead of their original one-drag fixed-count commit:
 *
 *   - Fixed-n drag-out (#928): press = first vertex (or the centre under
 *     Alt / Center snap), drag = first side / radius, release commits.
 *   - Pinned-size aim stage (#934): with Side length / Radius set a bare
 *     click anchors; the hover AIMS the fully determined polygon and the
 *     next click (or Enter / ✓) commits.
 *   - Auto-n two-stage flow (#931, Sides unset): the first drag — or a
 *     click, move, click — places only the first SIDE; stage 2 derives the
 *     side count from the cursor via the circumscribed circle through it,
 *     and the next click (or Enter / ✓) commits.
 *   - Pinned-circle variable count (#931): Sides unset + Radius set — the
 *     drag length picks the count on the fixed Ø = 2R circle.
 *   - Centre-mode variable count (#934): Sides unset + centre snap — the
 *     drag (or click-move-click) fixes the circle by its radius, then the
 *     cursor picks the first-side chord and the count follows; the next
 *     click (or Enter / ✓) commits.
 *
 * Split of responsibilities: the gesture owns the staged STATE plus the
 * shared feedback (segment ray, stage-2 circle-as-ray + count label, live
 * panel mirrors). The owning tool decides when the regular mode engages,
 * runs its own drag + point snapping, renders the primary ghost in its own
 * style via `renderPreview`, and commits the finished ring via
 * `commitVertices`.
 */
import type paper from "paper";
import type { PointerSample } from "../canvas/PointerInput";
import type { GuideRay, Scene } from "../canvas/Scene";
import {
  autoPolygonFromSide,
  autoPolygonOnCenteredCircle,
  autoPolygonOnPinnedCircle,
  regularPolygonFromCenter,
  regularPolygonFromSide,
  resolveRegularPolygonDrag,
  snapSegmentToLength,
} from "../model/regularPolygon";
import { cssVar } from "../colors";
import { isAngleSnapActive, snapToCommonAngle } from "./dragSnap";
import { setAngleHint } from "../snapAngleIndicator";
import { emitSegmentRay } from "./drawGesture";
import type { ToolSettings } from "./EditorContext";

export type Vec2 = { x: number; y: number };

/** What the owning tool should ghost for the in-flight gesture: the bare
 *  first side (auto stage 1) or the resolved ring (everything else). */
export type PolygonPreviewShape =
  | { kind: "line"; a: Vec2; b: Vec2 }
  | { kind: "poly"; vertices: Vec2[] };

export interface RegularPolygonGestureHost {
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getViewportView(): paper.View;
  getSettings(): ToolSettings;
  /** Render the primary ghost for the in-flight gesture in the tool's own
   *  style, replacing any previous ghost. */
  renderPreview(shape: PolygonPreviewShape): void;
  /** Remove the tool's ghost. */
  clearPreview(): void;
  /** Commit a finished ring (≥ 3 vertices, closed). */
  commitVertices(vertices: Vec2[]): void;
  /** Draft-state change → refresh the tool's finish / cancel fly-out. */
  notifyDraftChange(): void;
  /** Live panel mirrors (#931/#934) — optional; construction wires these to
   *  the polygon Sides / Radius / Side length fields. */
  liveSides?: (sides: number, radiusMm: number) => void;
  liveSideLength?: (mm: number) => void;
  /** Live Radius mirror for the centre-mode radius drag (#934). */
  liveRadius?: (mm: number) => void;
  liveClear?: () => void;
}

export class RegularPolygonGesture {
  /** Auto-n stage 2 (#931): the committed first SIDE of a regular polygon
   *  whose side count follows the cursor (Sides field unset). While set,
   *  hover previews the polygon and the next press commits it. */
  private autoPoly: {
    a: Vec2;
    b: Vec2;
    sides: number | null;
    /** Last stage-2 hover position — Enter commits the preview at it. */
    last?: Vec2;
  } | null = null;
  /** Auto-n stage 1 via click-move-click (#931): a plain click (no drag) in
   *  the regular mode anchors the side's first point; the hover then
   *  rubber-bands the side and the next click ends it (entering stage 2). */
  private autoSideStart: Vec2 | null = null;
  /** Hysteresis memory for the pinned-circle variable-count drag (#931). */
  private pinnedAutoSides: number | null = null;
  /** Fixed-n pinned-size aim stage (#934): a bare click with the size pinned
   *  (Side length / Radius set) anchors here; the hover then AIMS the fully
   *  determined polygon and the next click (or Enter) commits it. */
  private aimStart: {
    a: Vec2;
    fromCenter: boolean;
    last?: Vec2;
  } | null = null;
  /** Centre-mode variable count, stage-1 anchor (#934): a plain Alt / centre
   *  click with Sides unset anchors the CENTRE; the hover then rubber-bands
   *  the radius (a pinned Radius fixes its length — the hover only aims) and
   *  the next click fixes the circle's first vertex, entering stage 2. */
  private centerStart: Vec2 | null = null;
  /** Centre-mode variable count, stage 2 (#934): the circle is FIXED (centre
   *  + first vertex = the radius); the cursor's distance from the first
   *  vertex picks the first-side chord and the count follows
   *  (n = π/asin(L/D)). The next click (or Enter / ✓) commits. */
  private centerAuto: {
    c: Vec2;
    first: Vec2;
    sides: number | null;
    last?: Vec2;
  } | null = null;
  /** Gesture-owned auxiliary overlay: the stage-2 circle-as-ray + count
   *  label. The primary ghost is the TOOL's (via `renderPreview`). */
  private overlayItem: paper.Item | null = null;
  /** Modifiers seen last — release-time angle snap reads these (the tool's
   *  `onUp` sample can lack held keys on some platforms). */
  private lastSample: PointerSample | null = null;
  /** Last staged hover position — `refresh()` re-renders at it when a panel
   *  value changes mid-gesture (#934). */
  private lastHoverP: Vec2 | null = null;

  constructor(private readonly host: RegularPolygonGestureHost) {}

  /** True while any click-anchored stage is waiting for the next input.
   *  (The initial press-drag itself is the TOOL's state, not the gesture's.) */
  get stageActive(): boolean {
    return !!(
      this.autoPoly ||
      this.autoSideStart ||
      this.aimStart ||
      this.centerStart ||
      this.centerAuto
    );
  }

  /** Fly-out contract: the commit-ready stages (stage 2 / the aim stage)
   *  report 3 — at or above every tool's confirm gate (2 for construction /
   *  stitch, 3 for the leather Shape / Cutout polygon), so ✓ enables and
   *  Enter commits the preview. An anchored first point reports 1 (fly-out
   *  visible, ✓ disabled, ✗ discards); idle is 0. */
  vertexCount(): number {
    if (this.autoPoly || this.aimStart || this.centerAuto) return 3;
    if (this.autoSideStart || this.centerStart) return 1;
    return 0;
  }

  /** Angle modifier for POLYGON gestures: Ctrl / Cmd (or the Angle toggle)
   *  only — Shift already means "regular / equilateral" here, so it must not
   *  also lock the drag's angle (#931). */
  private angleSnap(s: PointerSample | null): boolean {
    return isAngleSnapActive(
      this.host.getSettings(),
      !!s && (s.ctrlKey || s.metaKey),
    );
  }

  /** Apply the polygon angle snap + pinned Side length to a side's end. */
  private constrainSideEnd(s: PointerSample | null, a: Vec2, b: Vec2): Vec2 {
    let out = { x: b.x, y: b.y };
    if (this.angleSnap(s)) {
      const sn = snapToCommonAngle(a, out);
      out = { x: sn.x, y: sn.y };
    }
    const pinL = this.host.getSettings().polygonSideLength;
    if (pinL != null && pinL > 0) out = snapSegmentToLength(a, out, pinL);
    return out;
  }

  /** Apply the polygon angle snap + the pinned Radius to a centre-mode
   *  radius vector's end (#934). */
  private constrainRadiusEnd(s: PointerSample | null, c: Vec2, p: Vec2): Vec2 {
    let out = { x: p.x, y: p.y };
    if (this.angleSnap(s)) {
      const sn = snapToCommonAngle(c, out);
      out = { x: sn.x, y: sn.y };
    }
    const pinR = this.host.getSettings().polygonRadius;
    if (pinR != null && pinR > 0) out = snapSegmentToLength(c, out, pinR);
    return out;
  }

  /** A press while a stage is active. Returns true when consumed (the tool
   *  must not fall through to its vertex / drag-start handling). */
  press(s: PointerSample, p: Vec2): boolean {
    this.lastSample = s;
    // Fixed-n aim stage (#934): the press commits the aimed polygon.
    if (this.aimStart) {
      const aim = this.aimStart;
      this.aimStart = null;
      this.teardownFeedback();
      this.host.notifyDraftChange();
      const pts = this.resolveVertices(aim.a, p, aim.fromCenter);
      if (pts.length >= 3) this.host.commitVertices(pts);
      return true;
    }
    // Auto-n stage 1 click-move-click (#931): the press ends the side.
    if (this.autoSideStart) {
      const a = this.autoSideStart;
      const b = this.constrainSideEnd(s, a, p);
      if (Math.hypot(b.x - a.x, b.y - a.y) > 0.5) {
        this.autoSideStart = null;
        this.autoPoly = { a, b, sides: null };
        this.teardownFeedback();
        setAngleHint(null);
        this.host.notifyDraftChange();
        this.updateAutoPolyPreview(this.seedPoint(a, b));
      }
      return true;
    }
    // Auto-n stage 2 (#931): the press commits the previewed polygon.
    if (this.autoPoly) {
      const r = this.stage2Result(p);
      this.autoPoly = null;
      this.teardownFeedback();
      this.host.notifyDraftChange();
      this.host.liveClear?.();
      if (r && r.vertices.length >= 3) this.host.commitVertices(r.vertices);
      return true;
    }
    // Centre-mode stage 1 click-move-click (#934): the press fixes the
    // circle's first vertex (= the radius), entering the count stage.
    if (this.centerStart) {
      const c = this.centerStart;
      const first = this.constrainRadiusEnd(s, c, p);
      if (Math.hypot(first.x - c.x, first.y - c.y) > 0.5) {
        this.centerStart = null;
        this.centerAuto = { c, first, sides: null };
        this.teardownFeedback();
        setAngleHint(null);
        this.host.notifyDraftChange();
        this.updateCenterAutoPreview(this.centerSeedPoint(c, first));
      }
      return true;
    }
    // Centre-mode stage 2 (#934): the press commits the previewed polygon.
    if (this.centerAuto) {
      const r = this.centerStage2Result(p);
      this.centerAuto = null;
      this.teardownFeedback();
      this.host.notifyDraftChange();
      this.host.liveClear?.();
      if (r && r.vertices.length >= 3) this.host.commitVertices(r.vertices);
      return true;
    }
    return false;
  }

  /** A hover while a stage is active. Returns true when consumed. */
  hover(s: PointerSample, p: Vec2): boolean {
    if (!this.stageActive) return false;
    this.lastSample = s;
    this.lastHoverP = { x: p.x, y: p.y };
    // Fixed-n aim stage (#934): the hover rotates the pinned polygon around
    // the anchored point; a click commits.
    if (this.aimStart) {
      this.aimStart.last = { x: p.x, y: p.y };
      this.renderAimPreview(p);
      return true;
    }
    // Auto-n stage 1 click-move-click (#931): rubber-band the side from the
    // anchored first point, with the same line preview + segment ray the
    // drag variant shows.
    if (this.autoSideStart) {
      this.renderSidePreview(p);
      return true;
    }
    // Centre-mode stage 1 click-move-click (#934): rubber-band the radius
    // from the anchored centre (a pinned Radius fixes its length).
    if (this.centerStart) {
      this.renderRadiusPreview(p);
      return true;
    }
    // Centre-mode stage 2 (#934): the hover picks the first-side chord on
    // the fixed circle and the count follows.
    if (this.centerAuto) {
      this.updateCenterAutoPreview(p);
      return true;
    }
    // Auto-n stage 2 (#931): the hover drives the live n preview — pulling
    // away from the first side grows the circumscribed circle and the side
    // count follows.
    this.updateAutoPolyPreview(p);
    return true;
  }

  /** Aim-stage preview at cursor `p` (uses the last-seen modifiers). */
  private renderAimPreview(p: Vec2): void {
    const aim = this.aimStart;
    if (!aim) return;
    const pts = this.resolveVertices(aim.a, p, aim.fromCenter);
    if (pts.length >= 3) {
      this.host.renderPreview({ kind: "poly", vertices: pts });
      // The aim still reads like drawing the first side / radius — show
      // its angle ray even though the length is pinned, plus the pinned
      // circle / radius vector / side count (#934; rays toggle gated).
      this.polyRingFeedback(aim.a, pts, aim.fromCenter);
    } else {
      this.host.clearPreview();
      this.host.getScene().setGuideRays(null);
      this.clearOverlay();
    }
  }

  /** Click-move-click side rubber-band preview at cursor `p`. */
  private renderSidePreview(p: Vec2): void {
    const a = this.autoSideStart;
    if (!a) return;
    const b = this.constrainSideEnd(this.lastSample, a, p);
    if (Math.hypot(b.x - a.x, b.y - a.y) > 0.5) {
      this.host.renderPreview({ kind: "line", a, b });
      emitSegmentRay(this.host.getScene(), a, b);
      this.host.liveSideLength?.(Math.hypot(b.x - a.x, b.y - a.y));
    } else {
      this.host.clearPreview();
      this.host.getScene().setGuideRays(null);
    }
  }

  /** Centre-mode click-move-click radius rubber-band at cursor `p` (#934). */
  private renderRadiusPreview(p: Vec2): void {
    const c = this.centerStart;
    if (!c) return;
    const b = this.constrainRadiusEnd(this.lastSample, c, p);
    this.renderRadiusFeedback(c, b);
  }

  /** Radius-vector feedback for the centre-mode variable count (#934),
   *  shared by the drag and the click-move-click hover: the tool's line
   *  ghost centre→vertex, an "R"-labelled aim ray, the circle the radius
   *  implies (rays toggle gated), and the live Radius mirror. */
  private renderRadiusFeedback(c: Vec2, b: Vec2): void {
    const r = Math.hypot(b.x - c.x, b.y - c.y);
    if (r <= 0.5) {
      this.host.clearPreview();
      this.host.getScene().setGuideRays(null);
      this.clearOverlay();
      return;
    }
    this.host.renderPreview({ kind: "line", a: c, b });
    // The drag draws the circumscribed RADIUS — label the aim ray as R
    // (keeping its angle), exactly like the fixed-ring centre mode does.
    const ray = this.segmentRay(c, b);
    delete ray.lengthMm;
    ray.radiusMm = r;
    this.host.getScene().setGuideRays([ray]);
    this.renderCircleOverlay({ x: c.x, y: c.y }, r, null, null);
    this.host.liveRadius?.(r);
  }

  /** Re-render the active stage at its last cursor — the owning tool calls
   *  this when a panel value (Sides / Radius / Side length / Equilateral)
   *  changes mid-gesture, so the preview and the rays update immediately
   *  instead of waiting for the next mouse move (#934). */
  refresh(): void {
    if (this.aimStart) {
      if (this.aimStart.last) this.renderAimPreview(this.aimStart.last);
      return;
    }
    if (this.autoSideStart) {
      if (this.lastHoverP) this.renderSidePreview(this.lastHoverP);
      return;
    }
    if (this.centerStart) {
      if (this.lastHoverP) this.renderRadiusPreview(this.lastHoverP);
      return;
    }
    if (this.centerAuto?.last) {
      this.updateCenterAutoPreview(this.centerAuto.last);
      return;
    }
    if (this.autoPoly?.last) this.updateAutoPolyPreview(this.autoPoly.last);
  }

  /** During-drag feedback for the tool's regular-mode drag: the ghost, the
   *  first-side segment ray, and the live panel mirrors. The tool passes its
   *  (already point-snapped) start / current positions. */
  dragFeedback(
    s: PointerSample | null,
    start: Vec2,
    current: Vec2,
    fromCenter: boolean,
  ): void {
    if (s) this.lastSample = s;
    const settings = this.host.getSettings();
    if (settings.polygonSides == null && fromCenter) {
      // Centre-mode variable count (#934): the drag is only the RADIUS —
      // preview the vector and the circle it implies (a pinned Radius fixes
      // the length so the drag only aims); the count stage follows release.
      const b = this.constrainRadiusEnd(s, start, current);
      this.renderRadiusFeedback(start, b);
      return;
    }
    if (
      settings.polygonSides == null &&
      settings.polygonRadius == null &&
      !fromCenter
    ) {
      // Auto-n mode (#931): stage 1 is only the first SIDE — preview a
      // plain line (with the same angle snap the release will apply), not
      // a default-count polygon that would flip shape on release.
      const b = this.constrainSideEnd(s, start, current);
      if (Math.hypot(b.x - start.x, b.y - start.y) > 0.5) {
        // The side drag reads like drawing a Line — show its length +
        // angle ray so the user can size the side precisely, and mirror
        // the live length into the Side length field (#934).
        this.host.renderPreview({ kind: "line", a: start, b });
        emitSegmentRay(this.host.getScene(), start, b);
        this.host.liveSideLength?.(Math.hypot(b.x - start.x, b.y - start.y));
      } else {
        this.host.clearPreview();
        this.host.getScene().setGuideRays(null);
      }
      return;
    }
    const pts = this.resolveVertices(start, current, fromCenter);
    if (pts.length >= 3) {
      this.host.renderPreview({ kind: "poly", vertices: pts });
      // The drag draws the first side (or the centre→vertex radius) — show
      // its angle ray even when a pinned Side length / Radius fixes the
      // length and the drag only aims, plus the pinned circle / radius
      // vector / side count (#934; rays toggle gates display).
      this.polyRingFeedback(start, pts, fromCenter);
    } else {
      this.host.clearPreview();
      this.host.getScene().setGuideRays(null);
      this.clearOverlay();
    }
  }

  /** The tool's regular-mode drag ended. Commits the fixed-count ring, or
   *  transitions into a stage (auto-n stage 2 / click-move-click anchor /
   *  pinned-size aim anchor). The tool has already cleared its own drag
   *  state before calling. */
  release(start: Vec2, current: Vec2, fromCenter: boolean): void {
    const settings = this.host.getSettings();
    // Centre-mode variable count (#934): Sides unset + centre snap — the
    // released drag fixed only the CIRCLE (centre + radius, a pinned Radius
    // pins the length); enter the count stage, where the cursor picks the
    // first-side chord and the next press commits. A plain click anchors
    // just the centre — the hover then rubber-bands the radius and the next
    // click fixes it (click-move-click, like the side variant).
    if (settings.polygonSides == null && fromCenter) {
      const rawDrag = Math.hypot(current.x - start.x, current.y - start.y);
      this.teardownFeedback();
      setAngleHint(null);
      if (rawDrag > 1) {
        const first = this.constrainRadiusEnd(this.lastSample, start, current);
        if (Math.hypot(first.x - start.x, first.y - start.y) > 0.5) {
          this.centerAuto = {
            c: { x: start.x, y: start.y },
            first,
            sides: null,
          };
          this.updateCenterAutoPreview(this.centerSeedPoint(start, first));
        }
      } else {
        this.centerStart = { x: start.x, y: start.y };
      }
      this.host.notifyDraftChange();
      return;
    }
    // Auto-n mode (#931): Sides unset, side gesture (no centre snap), no
    // pinned radius — the released drag is only the FIRST SIDE; enter
    // stage 2, where the hover derives the side count from the cursor and
    // the next press commits.
    if (
      settings.polygonSides == null &&
      settings.polygonRadius == null &&
      !fromCenter
    ) {
      // Click vs. drag is judged on the RAW distance — with a pinned Side
      // length, blowing a click's pixel-wobble up to a full side would aim
      // the segment at random (#934). A plain click anchors the point; the
      // hover then aims the (pinned-length) side and the next click ends it.
      const rawDrag = Math.hypot(current.x - start.x, current.y - start.y);
      this.teardownFeedback();
      setAngleHint(null);
      if (rawDrag > 1) {
        const b = this.constrainSideEnd(this.lastSample, start, current);
        this.autoPoly = { a: { x: start.x, y: start.y }, b, sides: null };
        this.updateAutoPolyPreview(this.seedPoint(start, b));
      } else {
        this.autoSideStart = { x: start.x, y: start.y };
      }
      this.host.notifyDraftChange();
      return;
    }
    // Fixed-n with the size pinned (#934): a bare click anchors — the hover
    // aims the fully determined polygon, the next click commits. Without
    // this, a click committed instantly pointing east.
    const sizePinned =
      settings.polygonSideLength != null || settings.polygonRadius != null;
    const rawDrag = Math.hypot(current.x - start.x, current.y - start.y);
    if (sizePinned && rawDrag <= 1) {
      this.aimStart = { a: { x: start.x, y: start.y }, fromCenter };
      this.teardownFeedback();
      setAngleHint(null);
      this.host.notifyDraftChange();
      return;
    }
    // Regular-polygon drag-out (#928): commit the full n-gon.
    const pts = this.resolveVertices(start, current, fromCenter);
    this.teardownFeedback();
    if (pts.length >= 3) this.host.commitVertices(pts);
  }

  /** Enter / ✓: commit the staged preview (stage 2 at the last hovered
   *  cursor; the aim stage at the last aimed direction — east by default).
   *  With only an anchored first point there is nothing to commit — abandon
   *  like Escape. Returns true when a stage consumed the finish. */
  finish(): boolean {
    if (this.autoPoly) {
      const ap = this.autoPoly;
      this.autoPoly = null;
      this.teardownFeedback();
      this.host.notifyDraftChange();
      this.host.liveClear?.();
      if (ap.last) {
        this.autoPoly = ap;
        const r = this.stage2Result(ap.last);
        this.autoPoly = null;
        if (r && r.vertices.length >= 3) this.host.commitVertices(r.vertices);
      }
      return true;
    }
    if (this.aimStart) {
      const aim = this.aimStart;
      this.aimStart = null;
      this.teardownFeedback();
      this.host.notifyDraftChange();
      const cursor = aim.last ?? { x: aim.a.x + 1, y: aim.a.y };
      const pts = this.resolveVertices(aim.a, cursor, aim.fromCenter);
      if (pts.length >= 3) this.host.commitVertices(pts);
      return true;
    }
    if (this.centerAuto) {
      const ca = this.centerAuto;
      this.centerAuto = null;
      this.teardownFeedback();
      this.host.notifyDraftChange();
      this.host.liveClear?.();
      if (ca.last) {
        this.centerAuto = ca;
        const r = this.centerStage2Result(ca.last);
        this.centerAuto = null;
        if (r && r.vertices.length >= 3) this.host.commitVertices(r.vertices);
      }
      return true;
    }
    if (this.autoSideStart || this.centerStart) {
      this.autoSideStart = null;
      this.centerStart = null;
      this.teardownFeedback();
      this.host.notifyDraftChange();
      return true;
    }
    return false;
  }

  /** Escape / ✗: abandon whatever stage is in flight. Returns true when a
   *  stage consumed it. */
  escape(): boolean {
    if (!this.stageActive) return false;
    this.autoPoly = null;
    this.autoSideStart = null;
    this.aimStart = null;
    this.centerStart = null;
    this.centerAuto = null;
    this.teardownFeedback();
    this.host.notifyDraftChange();
    this.host.liveClear?.();
    return true;
  }

  /** Unconditional teardown for the tool's full draft reset. Never calls
   *  `notifyDraftChange` — the tool's reset already does. */
  cancel(): void {
    this.autoPoly = null;
    this.autoSideStart = null;
    this.aimStart = null;
    this.centerStart = null;
    this.centerAuto = null;
    this.pinnedAutoSides = null;
    this.lastSample = null;
    this.lastHoverP = null;
    this.clearOverlay();
    this.host.liveClear?.();
  }

  /** Vertices of the resolved regular polygon for a start + cursor pair —
   *  applies the polygon angle snap, the pinned-circle variable-count flow
   *  (#931), and the pinned Side length / Radius (#934). */
  resolveVertices(
    startSrc: Vec2,
    cursorSrc: Vec2,
    fromCenter: boolean,
  ): Vec2[] {
    const settings = this.host.getSettings();
    const start = { x: startSrc.x, y: startSrc.y };
    let cursor = { x: cursorSrc.x, y: cursorSrc.y };
    // Angle snap constrains the drag vector's direction (Ctrl/Cmd or the
    // Angle toggle) so the polygon's first side / radius lands on a clean
    // drafting angle.
    if (this.angleSnap(this.lastSample)) {
      const sn = snapToCommonAngle(start, cursor);
      cursor = { x: sn.x, y: sn.y };
    }
    // Sides unset + Radius pinned (#931): the circle is fixed at ⌀ = 2R and
    // the drag's LENGTH picks the count — no hexagon default. The committed
    // side snaps to the integer count's exact chord.
    if (
      settings.polygonSides == null &&
      settings.polygonRadius != null &&
      !fromCenter
    ) {
      // Side length + Radius together fully determine the polygon (#934):
      // replace the drag length with the pinned side, so the drag only aims.
      let countCursor = cursor;
      const pinL = settings.polygonSideLength;
      if (pinL != null && pinL > 0) {
        const d = Math.hypot(cursor.x - start.x, cursor.y - start.y);
        if (d > 1e-9) {
          countCursor = {
            x: start.x + ((cursor.x - start.x) / d) * pinL,
            y: start.y + ((cursor.y - start.y) / d) * pinL,
          };
        }
      }
      const r = autoPolygonOnPinnedCircle(
        start,
        countCursor,
        2 * settings.polygonRadius,
        this.pinnedAutoSides,
      );
      this.pinnedAutoSides = r?.sides ?? null;
      return r?.vertices ?? [];
    }
    return resolveRegularPolygonDrag({
      start,
      cursor,
      sides: settings.polygonSides,
      radius: settings.polygonRadius,
      sideLength: settings.polygonSideLength,
      fromCenter,
    });
  }

  /** Seed cursor for the stage-2 preview the instant the side is placed
   *  (#931): the top of the semicircle over the side (smallest circle →
   *  the triangle), so the circle + polygon show before any mouse move. */
  private seedPoint(a: Vec2, b: Vec2): Vec2 {
    const L = Math.hypot(b.x - a.x, b.y - a.y);
    if (L < 1e-9) return a;
    return {
      x: (a.x + b.x) / 2 + ((b.y - a.y) / L) * (L / 2),
      y: (a.y + b.y) / 2 - ((b.x - a.x) / L) * (L / 2),
    };
  }

  /** Seed cursor for the centre-mode count stage the instant the circle is
   *  fixed (#934): the inscribed triangle's second vertex — the LONGEST
   *  chord that still closes a polygon — so the preview starts at the
   *  triangle (the same floor the side-mode stage 2 seeds) before any
   *  mouse move. */
  private centerSeedPoint(c: Vec2, first: Vec2): Vec2 {
    const ang = (2 * Math.PI) / 3;
    const dx = first.x - c.x;
    const dy = first.y - c.y;
    return {
      x: c.x + dx * Math.cos(ang) - dy * Math.sin(ang),
      y: c.y + dx * Math.sin(ang) + dy * Math.cos(ang),
    };
  }

  /** Visible-view cap for the auto-n circle's diameter (#931) — project mm.
   *  Position-aware: caps the circle's FARTHEST point (diametrically across
   *  the base, along the unfold direction) at the view boundary, so a base
   *  near a screen edge can't push the circle far off-view. With `avail` the
   *  distance from the base midpoint to the view edge along the unfold
   *  direction, the largest circle through the base still inside is
   *  D = (avail² + (L/2)²) / avail. Zoom out to allow a bigger polygon. */
  private maxDiameter(a: Vec2, b: Vec2, cursor: Vec2): number {
    const view = this.host.getViewportView().bounds;
    const fallback = Math.min(view.width, view.height);
    const L = Math.hypot(b.x - a.x, b.y - a.y);
    if (L < 1e-9) return fallback;
    const m = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    // Unfold direction: the base's perpendicular toward the cursor's side.
    let nx = -(b.y - a.y) / L;
    let ny = (b.x - a.x) / L;
    if ((cursor.x - m.x) * nx + (cursor.y - m.y) * ny < 0) {
      nx = -nx;
      ny = -ny;
    }
    // Distance from the midpoint along (nx, ny) to the view rectangle edge.
    const ts: number[] = [];
    if (nx > 1e-9) ts.push((view.x + view.width - m.x) / nx);
    else if (nx < -1e-9) ts.push((view.x - m.x) / nx);
    if (ny > 1e-9) ts.push((view.y + view.height - m.y) / ny);
    else if (ny < -1e-9) ts.push((view.y - m.y) / ny);
    const positive = ts.filter((t) => t > 0);
    if (!positive.length) return fallback;
    const avail = Math.min(...positive);
    const q = L / 2;
    if (avail <= q) return L; // boundary hugging the base — floor at n = 3
    return (avail * avail + q * q) / avail;
  }

  /** Stage-2 geometry for cursor `p`. Normally the cursor picks the count
   *  via the circumscribed circle through it — but panel values typed
   *  mid-stage take over (#934): a Side length re-pins the placed base, and
   *  a Sides value pins the count outright (the ring is then fully
   *  determined, unfolding toward the cursor's side of the base). */
  private stage2Result(p: Vec2): {
    sides: number;
    vertices: Vec2[];
    circle: { x: number; y: number; r: number };
  } | null {
    const ap = this.autoPoly;
    if (!ap) return null;
    const settings = this.host.getSettings();
    const pinL = settings.polygonSideLength;
    if (pinL != null && pinL > 0) ap.b = snapSegmentToLength(ap.a, ap.b, pinL);
    const n = settings.polygonSides;
    if (n != null) {
      // Unfold toward the cursor: `regularPolygonFromSide` builds left of
      // a→b in y-down space; the cursor is on that side when the cross
      // product is negative.
      const cross =
        (ap.b.x - ap.a.x) * (p.y - ap.a.y) - (ap.b.y - ap.a.y) * (p.x - ap.a.x);
      const vertices =
        cross <= 0
          ? regularPolygonFromSide(ap.a, ap.b, n)
          : regularPolygonFromSide(ap.b, ap.a, n);
      if (vertices.length < 3) return null;
      const cx = vertices.reduce((s2, v) => s2 + v.x, 0) / vertices.length;
      const cy = vertices.reduce((s2, v) => s2 + v.y, 0) / vertices.length;
      const r = Math.hypot(vertices[0]!.x - cx, vertices[0]!.y - cy);
      return { sides: n, vertices, circle: { x: cx, y: cy, r } };
    }
    return autoPolygonFromSide(
      ap.a,
      ap.b,
      p,
      ap.sides,
      this.maxDiameter(ap.a, ap.b, p),
    );
  }

  /** Centre-mode stage-2 geometry for cursor `p` (#934). The cursor's
   *  distance from the first vertex is the first-side chord — but panel
   *  values typed mid-stage take over, mirroring the side variant: a Radius
   *  re-pins the circle (keeping the first vertex's direction), a Side
   *  length pins the chord, and a Sides value pins the count outright. */
  private centerStage2Result(p: Vec2): {
    sides: number;
    vertices: Vec2[];
    radius: number;
  } | null {
    const ca = this.centerAuto;
    if (!ca) return null;
    const settings = this.host.getSettings();
    const pinR = settings.polygonRadius;
    if (pinR != null && pinR > 0)
      ca.first = snapSegmentToLength(ca.c, ca.first, pinR);
    const n = settings.polygonSides;
    if (n != null) {
      const vertices = regularPolygonFromCenter(ca.c, ca.first, n);
      if (vertices.length < 3) return null;
      return {
        sides: vertices.length,
        vertices,
        radius: Math.hypot(ca.first.x - ca.c.x, ca.first.y - ca.c.y),
      };
    }
    const pinL = settings.polygonSideLength;
    const chord =
      pinL != null && pinL > 0
        ? pinL
        : Math.hypot(p.x - ca.first.x, p.y - ca.first.y);
    return autoPolygonOnCenteredCircle(ca.c, ca.first, chord, ca.sides);
  }

  /** Centre-mode stage-2 preview (#934): the polygon ghost (via the tool),
   *  the FIXED circle drawn as a ray with the live count label, and the
   *  "R"-labelled radius vector. Called from the hover AND immediately on
   *  entering the stage, so the preview shows without a mouse move. */
  private updateCenterAutoPreview(p: Vec2): void {
    const ca = this.centerAuto;
    if (!ca) return;
    const r = this.centerStage2Result(p);
    this.clearOverlay();
    ca.last = { x: p.x, y: p.y };
    if (!r) {
      this.host.clearPreview();
      return;
    }
    ca.sides = r.sides;
    this.host.liveSides?.(r.sides, r.radius);
    this.host.liveSideLength?.(
      Math.hypot(
        r.vertices[1]!.x - r.vertices[0]!.x,
        r.vertices[1]!.y - r.vertices[0]!.y,
      ),
    );
    this.host.renderPreview({ kind: "poly", vertices: r.vertices });
    // The circle is FIXED here — the cursor only picks the chord — so the
    // radius vector (R label) plus the circle-as-ray and the live count
    // give the full picture (rays toggle gated; the scene gates the ray).
    this.host.getScene().setGuideRays([
      {
        from: { x: ca.c.x, y: ca.c.y },
        to: { x: ca.first.x, y: ca.first.y },
        radiusMm: r.radius,
      },
    ]);
    this.renderCircleOverlay(
      { x: ca.c.x, y: ca.c.y },
      r.radius,
      r.sides,
      r.vertices,
    );
  }

  /** Stage-2 preview (#931): the polygon ghost (via the tool) + the
   *  circle-as-ray + count overlay (gesture-owned) for cursor `p`. Called
   *  from the hover AND immediately on entering stage 2, so the preview
   *  shows without waiting for a mouse move. */
  private updateAutoPolyPreview(p: Vec2): void {
    if (!this.autoPoly) return;
    const r = this.stage2Result(p);
    this.clearOverlay();
    this.autoPoly.last = { x: p.x, y: p.y };
    if (!r) {
      this.host.clearPreview();
      return;
    }
    this.autoPoly.sides = r.sides;
    this.host.liveSides?.(r.sides, r.circle.r);
    this.host.renderPreview({ kind: "poly", vertices: r.vertices });
    // Continuous stage-2 feedback (#931, rays toggle gated): the cursor
    // drives the CIRCLE the polygon fits into, but between count jumps
    // nothing visibly changes — so draw that circumscribed circle live,
    // labelled with the current side count.
    const scene = this.host.getScene();
    if (!scene.isRaysEnabled()) return;
    const scope = this.host.getScope();
    // The smooth input circle passes THROUGH the cursor (three points
    // define it: the side's endpoints + the cursor), so a small sideways
    // move already widens it — capped at the visible view. Drawn AS the
    // ray (ray accent, dashed) — no separate radius vector; the circle
    // itself is the guide.
    const circle = new scope.Path.Circle(
      new scope.Point(r.circle.x, r.circle.y),
      r.circle.r,
    );
    circle.strokeColor = new scope.Color(
      cssVar(
        scene.isPointOverLeather({ x: r.circle.x, y: r.circle.y })
          ? "--canvas-accent-leather"
          : "--canvas-accent",
      ),
    );
    circle.strokeWidth = 1;
    circle.strokeScaling = false;
    circle.dashArray = [4, 3];
    circle.fillColor = null;
    circle.data = { ephemeral: true, kind: "auto-polygon-circle" };
    const group = new scope.Group([circle]);
    group.data = { ephemeral: true };
    // Side count, just inside the shape above the base segment's midpoint.
    const a0 = this.autoPoly.a;
    const b0 = this.autoPoly.b;
    const label = this.makeCountLabel(
      scope,
      circle.strokeColor,
      { x: (a0.x + b0.x) / 2, y: (a0.y + b0.y) / 2 },
      { x: r.circle.x, y: r.circle.y },
      r.sides,
    );
    if (label) group.addChild(label);
    this.overlayItem = group;
  }

  /** A plain length + angle segment ray (same fields `emitSegmentRay`
   *  emits), returned instead of set so it can compose with other rays. */
  private segmentRay(from: Vec2, to: Vec2): GuideRay {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    let deg = (Math.atan2(-dy, dx) * 180) / Math.PI;
    if (deg < 0) deg += 360;
    return {
      from: { x: from.x, y: from.y },
      to: { x: to.x, y: to.y },
      lengthMm: Math.hypot(dx, dy),
      angleDeg: deg,
    };
  }

  /** Full ray + overlay feedback for a resolved fixed ring (#934): the
   *  first-side / radius aim ray; with the Radius pinned, the circumscribed
   *  circle as a ray plus an "R"-labelled radius vector (centre mode folds
   *  the R label into the aim ray itself — it IS the radius); and the side
   *  count just inside the base, exactly like the auto-n stage-2 label. All
   *  of it rays-toggle gated (the scene gates `setGuideRays` itself). */
  private polyRingFeedback(
    start: Vec2,
    pts: Vec2[],
    fromCenter: boolean,
  ): void {
    const settings = this.host.getSettings();
    const r = settings.polygonRadius;
    const pinnedR = r != null && r > 0 ? r : null;
    // A regular ring's centroid IS its circumcentre; the radius is the
    // pinned value exactly (the resolver sizes the ring to it).
    const center = {
      x: pts.reduce((a, p) => a + p.x, 0) / pts.length,
      y: pts.reduce((a, p) => a + p.y, 0) / pts.length,
    };
    const aimRay = this.segmentRay(start, pts[fromCenter ? 0 : 1]!);
    const rays: GuideRay[] = [aimRay];
    if (pinnedR != null) {
      if (fromCenter) {
        // Centre mode: the aim ray IS the radius vector — label it as R.
        delete aimRay.lengthMm;
        aimRay.radiusMm = pinnedR;
      } else {
        rays.push({
          from: center,
          to: { x: pts[0]!.x, y: pts[0]!.y },
          radiusMm: pinnedR,
        });
      }
    }
    this.host.getScene().setGuideRays(rays);
    this.updatePolyOverlay(pts, center, pinnedR);
  }

  /** Gesture-owned overlay for a fixed ring: the pinned circumscribed circle
   *  (Radius set) drawn as a ray, and the side count just inside the shape
   *  above the base segment's midpoint — the same treatment the auto-n
   *  stage-2 preview uses. Rays toggle gated; cleared with the preview. */
  private updatePolyOverlay(
    pts: Vec2[],
    center: Vec2,
    pinnedR: number | null,
  ): void {
    this.clearOverlay();
    if (pts.length < 3) return;
    const scene = this.host.getScene();
    if (!scene.isRaysEnabled()) return;
    const scope = this.host.getScope();
    const color = new scope.Color(
      cssVar(
        scene.isPointOverLeather(center)
          ? "--canvas-accent-leather"
          : "--canvas-accent",
      ),
    );
    const group = new scope.Group();
    group.data = { ephemeral: true };
    if (pinnedR != null) {
      const circle = new scope.Path.Circle(
        new scope.Point(center.x, center.y),
        pinnedR,
      );
      circle.strokeColor = color;
      circle.strokeWidth = 1;
      circle.strokeScaling = false;
      circle.dashArray = [4, 3];
      circle.fillColor = null;
      circle.data = { ephemeral: true, kind: "pinned-radius-circle" };
      group.addChild(circle);
    }
    // Side count, just inside the shape above the first side's midpoint —
    // mirroring the auto-n stage-2 count label (#931).
    const label = this.makeCountLabel(
      scope,
      color,
      { x: (pts[0]!.x + pts[1]!.x) / 2, y: (pts[0]!.y + pts[1]!.y) / 2 },
      center,
      pts.length,
    );
    if (label) group.addChild(label);
    if (group.children.length) this.overlayItem = group;
    else group.remove();
  }

  /** Gesture-owned circle overlay for the centre-mode stages (#934): the
   *  centred circle drawn as a ray (dashed accent) — the radius drag's
   *  implied circle, then stage 2's fixed one with the side count just
   *  inside the first side's midpoint, the same treatment the side-mode
   *  stage 2 uses. Rays toggle gated; cleared with the preview. */
  private renderCircleOverlay(
    center: Vec2,
    radius: number,
    sides: number | null,
    vertices: Vec2[] | null,
  ): void {
    this.clearOverlay();
    const scene = this.host.getScene();
    if (!scene.isRaysEnabled()) return;
    const scope = this.host.getScope();
    const color = new scope.Color(
      cssVar(
        scene.isPointOverLeather(center)
          ? "--canvas-accent-leather"
          : "--canvas-accent",
      ),
    );
    const circle = new scope.Path.Circle(
      new scope.Point(center.x, center.y),
      radius,
    );
    circle.strokeColor = color;
    circle.strokeWidth = 1;
    circle.strokeScaling = false;
    circle.dashArray = [4, 3];
    circle.fillColor = null;
    circle.data = { ephemeral: true, kind: "auto-polygon-circle" };
    const group = new scope.Group([circle]);
    group.data = { ephemeral: true };
    if (sides != null && vertices && vertices.length >= 2) {
      const mid = {
        x: (vertices[0]!.x + vertices[1]!.x) / 2,
        y: (vertices[0]!.y + vertices[1]!.y) / 2,
      };
      const label = this.makeCountLabel(scope, color, mid, center, sides);
      if (label) group.addChild(label);
    }
    this.overlayItem = group;
  }

  /** The side-count label shared by every variable-count / pinned-ring
   *  preview: just inside the shape above `mid` (the base side's midpoint),
   *  offset toward `center` (always the body's side), so flipping the
   *  polygon to the other side of the base moves the number with it. Null
   *  when text is unavailable (headless canvas). */
  private makeCountLabel(
    scope: paper.PaperScope,
    color: paper.Color,
    mid: Vec2,
    center: Vec2,
    count: number,
  ): paper.PointText | null {
    try {
      const dxc = center.x - mid.x;
      const dyc = center.y - mid.y;
      const dl = Math.hypot(dxc, dyc);
      const zoom = scope.view?.zoom ?? 1;
      const off = 16 / zoom;
      const lx = dl > 1e-9 ? mid.x + (dxc / dl) * off : mid.x;
      const ly = dl > 1e-9 ? mid.y + (dyc / dl) * off : mid.y - off;
      const label = new scope.PointText({
        point: new scope.Point(lx, ly + 4 / zoom),
        content: `${count}`,
        fillColor: color,
        fontFamily: "sans-serif",
        fontSize: 12 / zoom,
        fontWeight: 700,
        justification: "center",
      });
      label.data = { ephemeral: true, kind: "auto-polygon-count" };
      return label;
    } catch {
      /* text unavailable in headless — the circle still renders */
      return null;
    }
  }

  private clearOverlay(): void {
    this.overlayItem?.remove();
    this.overlayItem = null;
  }

  /** Drop the ghost, the overlay, and the guide rays. */
  private teardownFeedback(): void {
    this.host.clearPreview();
    this.clearOverlay();
    this.host.getScene().clearGuideRays();
  }
}
